import React, { useState, useEffect } from 'react';
import LogViewer from './LogViewer';
import { logger } from '../utils/logger';

const DevTools: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showLogViewer, setShowLogViewer] = useState(false);
  const [logCounts, setLogCounts] = useState({
    errors: 0,
    warnings: 0,
    info: 0,
    debug: 0
  });

  useEffect(() => {
    // Only show in development or when explicitly enabled
    const shouldShow = process.env.NODE_ENV === 'development' || 
                      localStorage.getItem('enableDevTools') === 'true';
    setIsVisible(shouldShow);

    // Update log counts periodically
    const interval = setInterval(updateLogCounts, 5000);
    updateLogCounts(); // Initial update

    return () => clearInterval(interval);
  }, []);

  const updateLogCounts = () => {
    const logs = logger.getLogs();
    setLogCounts({
      errors: logs.filter(log => log.level === 'error').length,
      warnings: logs.filter(log => log.level === 'warn').length,
      info: logs.filter(log => log.level === 'info').length,
      debug: logs.filter(log => log.level === 'debug').length
    });
  };

  const enableDevTools = () => {
    localStorage.setItem('enableDevTools', 'true');
    setIsVisible(true);
  };

  const disableDevTools = () => {
    localStorage.removeItem('enableDevTools');
    setIsVisible(false);
  };

  const clearAllLogs = () => {
    logger.clearLogs();
    updateLogCounts();
  };

  const downloadLogs = () => {
    logger.downloadLogs();
  };

  // Keyboard shortcut to toggle dev tools (Ctrl+Shift+D)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        setIsVisible(!isVisible);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible]);

  if (!isVisible) {
    return (
      // Hidden trigger for production (triple-click to enable)
      <div
        onClick={(e) => {
          if (e.detail === 3) { // Triple-click
            enableDevTools();
          }
        }}
        className="fixed bottom-0 right-0 w-4 h-4 opacity-0 cursor-pointer"
        title="Triple-click to enable dev tools"
      />
    );
  }

  return (
    <>
      {/* Dev Tools Panel */}
      <div className="fixed bottom-4 right-4 bg-gray-900 text-white rounded-lg shadow-xl p-3 min-w-[200px] z-40">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold">Dev Tools</h3>
          <button
            onClick={disableDevTools}
            className="text-gray-400 hover:text-white text-xs"
            title="Disable dev tools"
          >
            ✕
          </button>
        </div>

        {/* Log Summary */}
        <div className="mb-3">
          <h4 className="text-xs font-semibold mb-2">Log Summary:</h4>
          <div className="grid grid-cols-2 gap-1 text-xs">
            <div className="flex justify-between">
              <span className="text-red-400">Errors:</span>
              <span>{logCounts.errors}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-yellow-400">Warnings:</span>
              <span>{logCounts.warnings}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-400">Info:</span>
              <span>{logCounts.info}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Debug:</span>
              <span>{logCounts.debug}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-2">
          <button
            onClick={() => setShowLogViewer(true)}
            className="w-full px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs"
          >
            View Logs
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={clearAllLogs}
              className="px-2 py-1 bg-red-600 hover:bg-red-700 rounded text-xs"
            >
              Clear
            </button>
            <button
              onClick={downloadLogs}
              className="px-2 py-1 bg-green-600 hover:bg-green-700 rounded text-xs"
            >
              Download
            </button>
          </div>
        </div>

        {/* App Info */}
        <div className="mt-3 pt-3 border-t border-gray-700">
          <div className="text-xs text-gray-400">
            <div>Env: {process.env.NODE_ENV}</div>
            <div>Version: {process.env.REACT_APP_VERSION || 'dev'}</div>
            <div>Session: {logger.getLogs()[0]?.sessionId?.slice(-8) || 'N/A'}</div>
          </div>
        </div>

        {/* Keyboard Shortcuts */}
        <div className="mt-2 pt-2 border-t border-gray-700">
          <div className="text-xs text-gray-400">
            <div>Ctrl+Shift+D: Toggle</div>
          </div>
        </div>
      </div>

      {/* Log Viewer Modal */}
      <LogViewer
        isVisible={showLogViewer}
        onClose={() => setShowLogViewer(false)}
      />
    </>
  );
};

export default DevTools;